import type { BackendTypes } from '@tf/api';
import type { SearchHit } from '@tf/shared';
import { getResultHit, ScreeningResultCard } from '@tf/shared';
import {
	Box,
	Button,
	Center,
	createStyles,
	Flex,
	Icon,
	Stack,
	TFDataTable,
	TFText,
	Title,
	UnstyledButton,
} from '@tf/ui';
import { S, T } from '@tf/utils';
import type React from 'react';
import { useState } from 'react';

import { JobResultComments } from '../JobResultComments';

const useStyles = createStyles(({ colors, radius }) => ({
	infoBlockWrapper: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '.75rem .75rem',
		borderRadius: radius.sm,
		backgroundColor: colors.gray[1],
	},
	infoBlockContent: {
		flex: 1,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginRight: '.5rem',
		cursor: 'default',
		color: colors.dark[7],
	},
	fullName: {
		fontSize: 13,
		height: 30,
		display: 'flex',
		alignItems: 'center',
		color: colors.gray[9],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
	backButton: {
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[7],
		},
	},
}));

export interface Matches {
	ids: string[];
	comments: BackendTypes.AdhocComment[];
}

interface Props {
	hits: SearchHit[];
	matches: Matches;
	onUpdateMatches: (matches: Matches) => void;
}

export const JobResultTable: React.FC<Props> = ({ hits, matches, onUpdateMatches }) => {
	const { classes } = useStyles();

	const [isCommentsVisible, setIsCommentsVisible] = useState(matches.comments.length > 0);

	const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

	if (hits.length === 0) {
		return (
			<Center h={150}>
				<Stack align="center" gap="sm">
					<Title order={4} c="dimmed">
						No records found
					</Title>
				</Stack>
			</Center>
		);
	}

	const toggleMatch = (id: string) => {
		const isSelected = matches.ids.includes(id);
		const newIds = isSelected ? matches.ids.filter((i) => i !== id) : [...matches.ids, id];
		onUpdateMatches({ ids: newIds, comments: [] });
		setIsCommentsVisible(false);
	};

	const selectNone = () => {
		onUpdateMatches({ ids: [], comments: [] });
		setIsCommentsVisible(true);
	};

	const updateComments = (comments: BackendTypes.AdhocComment[]) => {
		onUpdateMatches({ ids: [], comments });
	};

	if (selectedCardId) {
		const selectedHit = hits.find((h) => h.doc.id === selectedCardId);
		T.assertNotNullish(selectedHit, 'no hit found for selected card');

		const isMarkedAsMatch = matches.ids.includes(selectedCardId);

		return (
			<Stack pt="0.5rem">
				<UnstyledButton onClick={() => setSelectedCardId(null)}>
					<Flex className={classes.backButton}>
						<Icon.IconChevronLeft size={18} />
						Back
					</Flex>
				</UnstyledButton>
				<ScreeningResultCard hit={getResultHit(selectedHit)} withInnerCard={false} />
				<Button variant="light" onClick={() => toggleMatch(selectedCardId)} color={isMarkedAsMatch ? 'gray' : 'indigo'}>
					<TFText inherit>{isMarkedAsMatch ? 'Remove match' : "It's match"}</TFText>
				</Button>
			</Stack>
		);
	}

	const profiles = hits.map((item) => {
		const uniqueDatesOfBirth = new Set<string>(
			item.doc.fields?.length
				? item.doc.fields.filter((field) => field.tag === 'date_of_birth').map((field) => field.value)
				: [],
		);
		return {
			id: item.doc.id,
			entityType: item.doc.entity_type,
			fullName: item.doc.name,
			countries: item.doc.fields ? item.doc.fields.filter((i) => i.tag === 'country_names').map((i) => i.value) : [],
			dateOfBirth: uniqueDatesOfBirth.size ? Array.from(uniqueDatesOfBirth).join(', ') : '',
		};
	});

	return (
		<Stack>
			<TFDataTable
				name="Matches list"
				data={profiles}
				paperProps={{ sx: { borderRadius: '8px' } }}
				defs={[
					{
						header: 'Full name',
						accessorKey: 'fullName',
						enableSorting: true,
						size: 200,
						Cell: ({ row }) => {
							const { fullName, id } = row.original;
							return (
								<UnstyledButton
									className={classes.fullName}
									onClick={() => {
										setSelectedCardId(id);
									}}
								>
									<TFText inherit lineClamp={1}>
										{fullName || 'N/A'}
									</TFText>
								</UnstyledButton>
							);
						},
					},
					{
						header: 'Entity type',
						accessorKey: 'entityType',
						size: 100,
						Cell: ({ row }) => {
							const { entityType } = row.original;
							return (
								<TFText inherit lineClamp={1}>
									{S.prettify(entityType) || 'N/A'}
								</TFText>
							);
						},
					},
					{
						header: 'Countries',
						accessorKey: 'countries',
						size: 200,
						enableSorting: true,
						Cell: ({ row }) => {
							const { countries } = row.original;
							return (
								<div>
									{countries?.length
										? countries.map((item: string, index: number) => (
												<Box key={`${item}-${index}`}>
													<TFText inherit>{item || 'N/A'}</TFText>
												</Box>
											))
										: 'N/A'}
								</div>
							);
						},
					},
					{
						header: 'Date of birth',
						accessorKey: 'dateOfBirth',
						size: 100,
						Cell: ({ row }) => {
							const { dateOfBirth } = row.original;
							if (dateOfBirth) {
								return (
									<TFText inherit lineClamp={1}>
										{dateOfBirth}
									</TFText>
								);
							} else {
								return (
									<TFText inherit c="dimmed" lineClamp={1}>
										N/A
									</TFText>
								);
							}
						},
					},
					{
						header: 'Action',
						accessorKey: 'id',
						enableSorting: false,
						enableColumnDragging: false,
						enableResizing: false,
						size: 135,
						maxSize: 135,
						Cell: ({ row }) => {
							const { id } = row.original;
							const isMarkedAsMatch = matches.ids.includes(id);
							return (
								<Button
									color={isMarkedAsMatch ? 'gray' : 'rgba(37, 99, 235, 0.08)'}
									fullWidth
									variant="light"
									onClick={() => toggleMatch(id)}
								>
									<TFText c={isMarkedAsMatch ? 'gray' : 'brand'} inherit>
										{isMarkedAsMatch ? 'Unmatch' : 'Match'}
									</TFText>
								</Button>
							);
						},
					},
				]}
			/>
			{isCommentsVisible ? (
				<JobResultComments comments={matches.comments} onUpdateComments={updateComments} />
			) : (
				<Button variant="light" onClick={selectNone}>
					<TFText inherit>None of the above</TFText>
				</Button>
			)}
		</Stack>
	);
};
